import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Wallet, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Send, 
  QrCode,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Check,
  Clock,
  FileText,
  ShieldAlert,
  Search,
  Key,
  AlertTriangle,
} from 'lucide-react';
import { Card, Button, Input, Badge, Modal, Select } from '../components/ui';
import { ContractStatusBadge, KycStatusBadge } from '../components/ui/ContractStatusBadge';
import { VirtualAccountContractModal } from '../components/admin/VirtualAccountContractModal';
import { PixKeyFormModal } from '../components/virtual-accounts/PixKeyFormModal';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth.store';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

interface VirtualAccount {
  id: string;
  tenantId: string;
  userId: string;
  escrowAccountId: string;
  pixKey: string | null;
  pixKeyType: string | null;
  status: string;
  createdAt: string;
  user?: { name: string; email: string };
  // Contract control fields
  contractStatus?: string;
  kycOriginStatus?: string;
  kycDestinationStatus?: string;
}

interface LedgerEntry {
  id: string;
  type: string;
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  correlationId: string;
  description: string;
  createdAt: string;
}

export function VirtualAccounts() {
  const queryClient = useQueryClient();
  const { user, isTenantAdmin, isSuperAdmin } = useAuthStore();
  const [selectedAccount, setSelectedAccount] = useState<VirtualAccount | null>(null);
  const [showBalance, setShowBalance] = useState(true);
  const [copiedKey, setCopiedKey] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPixChargeModal, setShowPixChargeModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedAccountForContract, setSelectedAccountForContract] = useState<VirtualAccount | null>(null);
  const [showPixKeyModal, setShowPixKeyModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Check if user can perform financial operations on the selected account
  // Admin can VIEW all accounts but can only OPERATE on their own account
  const canOperateOnAccount = (account: VirtualAccount | null): boolean => {
    if (!account || !user) return false;
    
    // Super Admin can operate on any account (for testing/support purposes)
    if (isSuperAdmin()) return true;
    
    // If user is Admin/Operator/Compliance, they can only operate on their OWN account
    if (isTenantAdmin()) {
      return account.userId === user.id;
    }
    
    // Correntistas can only operate on their own account
    return account.userId === user.id;
  };

  // Check if user is viewing someone else's account (for showing warning)
  const isViewingOthersAccount = (account: VirtualAccount | null): boolean => {
    if (!account || !user) return false;
    return account.userId !== user.id && !isSuperAdmin();
  };

  // Fetch virtual accounts with search
  const { data: accountsData, isLoading } = useQuery({
    queryKey: ['virtual-accounts', debouncedSearch],
    queryFn: () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      const url = `${ENDPOINTS.virtualAccounts.list}${params.toString() ? '?' + params.toString() : ''}`;
      return api.get(url).then(res => res.data);
    },
  });
  
  // Extract accounts array from response
  const accounts = accountsData?.accounts || [];

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['virtual-account-stats'],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.stats).then(res => res.data),
  });

  // Fetch balance for selected account
  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['virtual-account-balance', selectedAccount?.id],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.balance(selectedAccount!.id)).then(res => res.data),
    enabled: !!selectedAccount,
  });

  // Fetch statement for selected account — always load when account is selected
  const { data: statement } = useQuery({
    queryKey: ['virtual-account-statement', selectedAccount?.id],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.statement(selectedAccount!.id)).then(res => res.data),
    enabled: !!selectedAccount,
  });

  const copyPixKey = () => {
    if (selectedAccount?.pixKey) {
      navigator.clipboard.writeText(selectedAccount.pixKey);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      ACTIVE: 'success',
      BLOCKED: 'error',
      CLOSED: 'warning',
    };
    return <Badge variant={variants[status] || 'warning'}>{status}</Badge>;
  };

  const getEntryTypeIcon = (type: string) => {
    switch (type) {
      case 'CREDIT':
        return <ArrowDownLeft className="w-4 h-4 text-green-500" />;
      case 'DEBIT':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'BLOCK':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'UNBLOCK':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Contas Virtuais</h1>
          <p className="text-surface-400 mt-1">Gerencie suas contas virtuais com ledger interno</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta Virtual
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-surface-400">Total de Contas</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-surface-400">Com Chave PIX</p>
            <p className="text-2xl font-bold text-green-400">{stats.withPixKey}</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <p className="text-sm text-surface-400">Sem Chave PIX</p>
              {stats.withoutPixKey > 0 && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.withoutPixKey}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-surface-400">Ativas</p>
            <p className="text-2xl font-bold text-primary-400">
              {stats.byStatus?.find((s: any) => s.status === 'ACTIVE')?.count || 0}
            </p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account List */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Suas Contas</h3>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome..."
                className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:border-primary-500 text-sm"
              />
            </div>
            
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-surface-800 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : accounts?.length === 0 ? (
              <div className="text-center py-8 text-surface-400">
                <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{debouncedSearch ? 'Nenhuma conta encontrada' : 'Nenhuma conta virtual encontrada'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {accounts?.map((account: VirtualAccount) => (
                  <div
                    key={account.id}
                    onClick={() => setSelectedAccount(account)}
                    className={cn(
                      'p-4 rounded-lg border cursor-pointer transition-all',
                      selectedAccount?.id === account.id
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <Wallet className="w-5 h-5 text-primary-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {account.user?.name || 'Conta Virtual'}
                          </p>
                          <p className="text-sm text-surface-400">
                            {account.pixKey ? `Pix: ${account.pixKey.slice(0, 8)}...` : (
                              <span className="flex items-center gap-1 text-yellow-400">
                                <AlertTriangle className="w-3 h-3" />
                                Sem chave Pix
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {getStatusBadge(account.status)}
                        {account.contractStatus && (
                          <ContractStatusBadge status={account.contractStatus} size="sm" />
                        )}
                      </div>
                    </div>
                    {/* Contract KYC Status */}
                    {(account.kycOriginStatus || account.kycDestinationStatus) && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-surface-700">
                        <span className="text-xs text-surface-500">KYC:</span>
                        {account.kycOriginStatus && (
                          <KycStatusBadge status={account.kycOriginStatus} type="origin" size="sm" />
                        )}
                        {account.kycDestinationStatus && (
                          <KycStatusBadge status={account.kycDestinationStatus} type="destination" size="sm" />
                        )}
                      </div>
                    )}
                    {/* Contract Management Button */}
                    <div className="mt-2 pt-2 border-t border-surface-700">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAccountForContract(account);
                          setShowContractModal(true);
                        }}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Gerenciar Contrato
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Account Details */}
        <div className="lg:col-span-2 space-y-4">
          {selectedAccount ? (
            <>
              {/* Balance Card */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Saldo</h3>
                  <button
                    onClick={() => setShowBalance(!showBalance)}
                    className="text-surface-400 hover:text-white transition-colors"
                  >
                    {showBalance ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {balanceLoading ? (
                  <div className="h-24 bg-surface-800 rounded-lg animate-pulse" />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-surface-400 mb-1">Disponível</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-400 truncate">
                        {showBalance ? formatCurrency(Number(balance?.available || 0)) : '••••••'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-400 mb-1">Bloqueado</p>
                      <p className="text-xl sm:text-2xl font-bold text-yellow-400 truncate">
                        {showBalance ? formatCurrency(Number(balance?.blocked || 0)) : '••••••'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-surface-400 mb-1">Total</p>
                      <p className="text-xl sm:text-2xl font-bold text-white truncate">
                        {showBalance ? formatCurrency(Number(balance?.total || 0)) : '••••••'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Pix Key */}
                <div className="mt-6 p-4 bg-surface-800/50 rounded-lg">
                  {selectedAccount.pixKey ? (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-surface-400">Chave Pix ({selectedAccount.pixKeyType})</p>
                        <p className="font-mono text-white text-sm sm:text-base break-all">{selectedAccount.pixKey}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={copyPixKey}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                        >
                          {copiedKey ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy className="w-5 h-5 text-surface-400" />
                          )}
                        </button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPixKeyModal(true)}
                          disabled={!canOperateOnAccount(selectedAccount)}
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
                        <div>
                          <p className="text-sm text-yellow-400 font-medium">Sem Chave PIX</p>
                          <p className="text-xs text-surface-500">Cadastre uma chave para receber pagamentos</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => setShowPixKeyModal(true)}
                        disabled={!canOperateOnAccount(selectedAccount)}
                      >
                        <Key className="w-4 h-4 mr-2" />
                        Cadastrar Chave PIX
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              {/* Custom Cash-In Fee (Admin only) */}
              {(isTenantAdmin?.() || isSuperAdmin?.()) && (
                <CustomFeeCard accountId={selectedAccount.id} />
              )}

              {/* Quick Actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Warning banner when viewing someone else's account */}
                {isViewingOthersAccount(selectedAccount) && (
                  <div className="col-span-full mb-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-3">
                    <ShieldAlert className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-400">Modo Visualização</p>
                      <p className="text-xs text-surface-400">
                        Você está visualizando a conta de outro usuário. Operações financeiras estão desabilitadas.
                      </p>
                    </div>
                  </div>
                )}
                <Button
                  variant="secondary"
                  className="flex flex-col items-center py-6"
                  onClick={() => setShowPixChargeModal(true)}
                  disabled={!canOperateOnAccount(selectedAccount)}
                  title={!canOperateOnAccount(selectedAccount) ? 'Você não pode operar nesta conta' : undefined}
                >
                  <QrCode className="w-6 h-6 mb-2" />
                  <span>Receber Pix</span>
                </Button>
                <Button
                  variant="secondary"
                  className="flex flex-col items-center py-6"
                  onClick={() => setShowTransferModal(true)}
                  disabled={!canOperateOnAccount(selectedAccount)}
                  title={!canOperateOnAccount(selectedAccount) ? 'Você não pode operar nesta conta' : undefined}
                >
                  <Send className="w-6 h-6 mb-2" />
                  <span>Transferir</span>
                </Button>
                <Button
                  variant="secondary"
                  className="flex flex-col items-center py-6"
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={!canOperateOnAccount(selectedAccount)}
                  title={!canOperateOnAccount(selectedAccount) ? 'Você não pode operar nesta conta' : undefined}
                >
                  <ArrowUpRight className="w-6 h-6 mb-2" />
                  <span>Sacar</span>
                </Button>
                <Button
                  variant="secondary"
                  className="flex flex-col items-center py-6"
                  onClick={() => setShowStatementModal(true)}
                >
                  <Clock className="w-6 h-6 mb-2" />
                  <span>Extrato</span>
                </Button>
              </div>

              {/* Recent Transactions */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Últimas Movimentações</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowStatementModal(true)}>
                    Extrato completo
                  </Button>
                </div>

                {statement?.entries?.slice(0, 15).map((entry: LedgerEntry) => (
                  <div
                    key={entry.id}
                    className="flex items-start sm:items-center justify-between gap-2 py-3 border-b border-surface-700 last:border-0"
                  >
                    <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0">
                      <span className="mt-0.5 sm:mt-0 shrink-0">{getEntryTypeIcon(entry.type)}</span>
                      <div className="min-w-0">
                        <p className="text-sm sm:text-base text-white break-words leading-snug">{entry.description}</p>
                        <p className="text-xs sm:text-sm text-surface-400">{formatDate(entry.createdAt)}</p>
                      </div>
                    </div>
                    <p className={cn(
                      'text-sm sm:text-base font-medium shrink-0',
                      entry.type === 'CREDIT' ? 'text-green-400' : 
                      entry.type === 'DEBIT' ? 'text-red-400' : 'text-surface-400'
                    )}>
                      {entry.type === 'CREDIT' ? '+' : entry.type === 'DEBIT' ? '-' : ''}
                      {formatCurrency(Number(entry.amount))}
                    </p>
                  </div>
                )) || (
                  <p className="text-center text-surface-400 py-4">Nenhuma movimentação</p>
                )}
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-surface-600" />
              <h3 className="text-xl font-semibold text-white mb-2">Selecione uma conta</h3>
              <p className="text-surface-400">
                Escolha uma conta virtual na lista ao lado para ver detalhes e realizar operações
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
          setShowCreateModal(false);
        }}
      />

      {/* Transfer Modal */}
      {selectedAccount && (
        <TransferModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          accountId={selectedAccount.id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['virtual-account-balance'] });
            queryClient.invalidateQueries({ queryKey: ['virtual-account-statement'] });
            setShowTransferModal(false);
          }}
        />
      )}

      {/* Withdraw Modal */}
      {selectedAccount && (
        <WithdrawModal
          isOpen={showWithdrawModal}
          onClose={() => setShowWithdrawModal(false)}
          accountId={selectedAccount.id}
          availableBalance={Number(balance?.available || 0)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['virtual-account-balance'] });
            queryClient.invalidateQueries({ queryKey: ['virtual-account-statement'] });
            setShowWithdrawModal(false);
          }}
        />
      )}

      {/* Pix Charge Modal */}
      {selectedAccount && (
        <PixChargeModal
          isOpen={showPixChargeModal}
          onClose={() => setShowPixChargeModal(false)}
          accountId={selectedAccount.id}
        />
      )}

      {/* Statement Modal */}
      {selectedAccount && (
        <StatementModal
          isOpen={showStatementModal}
          onClose={() => setShowStatementModal(false)}
          accountId={selectedAccount.id}
          entries={statement?.entries || []}
        />
      )}

      {/* Contract Modal */}
      {selectedAccountForContract && (
        <VirtualAccountContractModal
          isOpen={showContractModal}
          onClose={() => {
            setShowContractModal(false);
            setSelectedAccountForContract(null);
          }}
          virtualAccountId={selectedAccountForContract.id}
          virtualAccountName={selectedAccountForContract.user?.name || 'Conta Virtual'}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
          }}
        />
      )}

      {/* Pix Key Form Modal */}
      {selectedAccount && (
        <PixKeyFormModal
          isOpen={showPixKeyModal}
          onClose={() => setShowPixKeyModal(false)}
          accountId={selectedAccount.id}
          currentPixKey={selectedAccount.pixKey}
          currentPixKeyType={selectedAccount.pixKeyType}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['virtual-accounts'] });
          }}
        />
      )}
    </div>
  );
}

// Create Account Modal Component
function CreateAccountModal({ 
  isOpen, 
  onClose, 
  onSuccess 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSuccess: () => void;
}) {
  const [userId, setUserId] = useState('');
  const [escrowAccountId, setEscrowAccountId] = useState('');
  const [pixKeyType, setPixKeyType] = useState<string>('');

  // Fetch users for selection
  const { data: usersData } = useQuery({
    queryKey: ['users-for-virtual-account'],
    queryFn: () => api.get(ENDPOINTS.users.list).then(res => res.data),
    enabled: isOpen,
  });
  const users = usersData?.users || usersData || [];

  // Fetch bolsão accounts
  const { data: escrowAccountsData } = useQuery({
    queryKey: ['escrow-accounts'],
    queryFn: () => api.get(ENDPOINTS.accounts.list).then(res => res.data),
    enabled: isOpen,
  });
  
  // Extract accounts array - filter only BOLSAO type accounts
  const escrowAccounts = escrowAccountsData?.accounts?.filter((a: any) => a.type === 'BOLSAO') || [];

  const createMutation = useMutation({
    mutationFn: (data: { userId: string; escrowAccountId: string; pixKeyType?: string }) =>
      api.post(ENDPOINTS.virtualAccounts.create, data),
    onSuccess: () => {
      setUserId('');
      setEscrowAccountId('');
      setPixKeyType('');
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: { userId: string; escrowAccountId: string; pixKeyType?: string } = {
      userId,
      escrowAccountId,
    };
    if (pixKeyType) {
      payload.pixKeyType = pixKeyType;
    }
    createMutation.mutate(payload);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova Conta Virtual">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Usuário (Correntista)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          required
        >
          <option value="">Selecione um usuário</option>
          {Array.isArray(users) && users.map((user: any) => (
            <option key={user.id} value={user.id}>
              {user.name} - {user.email}
            </option>
          ))}
        </Select>

        <Select
          label="Conta Escrow (Bolsão)"
          value={escrowAccountId}
          onChange={(e) => setEscrowAccountId(e.target.value)}
          required
        >
          <option value="">Selecione uma conta bolsão</option>
          {escrowAccounts.map((account: any) => (
            <option key={account.id} value={account.id}>
              {account.name} - {account.type}
            </option>
          ))}
        </Select>

        <Select
          label="Tipo de Chave Pix (opcional)"
          value={pixKeyType}
          onChange={(e) => setPixKeyType(e.target.value)}
        >
          <option value="">Sem chave Pix</option>
          <option value="CPF">CPF</option>
          <option value="CNPJ">CNPJ</option>
          <option value="EMAIL">E-mail</option>
          <option value="PHONE">Telefone</option>
          <option value="RANDOM">Chave Aleatória</option>
        </Select>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={createMutation.isPending || !userId || !escrowAccountId}>
            {createMutation.isPending ? 'Criando...' : 'Criar Conta'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Transfer Modal Component
function TransferModal({
  isOpen,
  onClose,
  accountId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  onSuccess: () => void;
}) {
  const [toAccountId, setToAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const { data: accountsData } = useQuery({
    queryKey: ['virtual-accounts'],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.list).then(res => res.data),
    enabled: isOpen,
  });
  
  const accounts = accountsData?.accounts || [];

  const transferMutation = useMutation({
    mutationFn: (data: { toAccountId: string; amount: string; description?: string }) =>
      api.post(ENDPOINTS.virtualAccounts.transfer(accountId), data),
    onSuccess: () => {
      setToAccountId('');
      setAmount('');
      setDescription('');
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transferMutation.mutate({
      toAccountId,
      amount: Math.round(parseFloat(amount) * 100).toString(),
      description: description || undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transferir">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Conta Destino"
          value={toAccountId}
          onChange={(e) => setToAccountId(e.target.value)}
          required
        >
          <option value="">Selecione uma conta</option>
          {accounts?.filter((a: VirtualAccount) => a.id !== accountId).map((account: VirtualAccount) => (
            <option key={account.id} value={account.id}>
              {account.user?.name || account.id.slice(0, 8)}
            </option>
          ))}
        </Select>

        <Input
          label="Valor (R$)"
          type="number"
          step="0.01"
          min="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />

        <Input
          label="Descrição"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex: Pagamento de serviço"
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={transferMutation.isPending}>
            {transferMutation.isPending ? 'Transferindo...' : 'Transferir'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Withdraw Modal Component
function WithdrawModal({
  isOpen,
  onClose,
  accountId,
  availableBalance,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  availableBalance: number;
  onSuccess: () => void;
}) {
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('CPF');
  const [amount, setAmount] = useState('');

  const withdrawMutation = useMutation({
    mutationFn: (data: { pixKey: string; pixKeyType: string; amount: string; description?: string }) =>
      api.post(ENDPOINTS.virtualAccounts.withdraw(accountId), data),
    onSuccess: () => {
      setPixKey('');
      setPixKeyType('CPF');
      setAmount('');
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    withdrawMutation.mutate({
      pixKey,
      pixKeyType,
      amount: Math.round(parseFloat(amount) * 100).toString(),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sacar via Pix">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-surface-800/50 rounded-lg mb-4">
          <p className="text-sm text-surface-400">Saldo disponível</p>
          <p className="text-xl font-bold text-green-400">
            {formatCurrency(availableBalance)}
          </p>
        </div>

        <Select
          label="Tipo de Chave"
          value={pixKeyType}
          onChange={(e) => setPixKeyType(e.target.value)}
        >
          <option value="CPF">CPF</option>
          <option value="CNPJ">CNPJ</option>
          <option value="EMAIL">E-mail</option>
          <option value="PHONE">Telefone</option>
          <option value="RANDOM">Chave Aleatória</option>
        </Select>

        <Input
          label="Chave Pix"
          value={pixKey}
          onChange={(e) => setPixKey(e.target.value)}
          placeholder="Digite a chave Pix de destino"
          required
        />

        <Input
          label="Valor (R$)"
          type="number"
          step="0.01"
          min="0.01"
          max={availableBalance / 100}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={withdrawMutation.isPending}>
            {withdrawMutation.isPending ? 'Processando...' : 'Sacar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Pix Charge Modal Component
function PixChargeModal({
  isOpen,
  onClose,
  accountId,
}: {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
}) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [qrCode, setQrCode] = useState<{ brCode: string; qrCodeImage: string } | null>(null);

  const chargeMutation = useMutation({
    mutationFn: (data: { value: string; description?: string }) =>
      api.post(ENDPOINTS.virtualAccounts.pixCharge(accountId), data),
    onSuccess: (response) => {
      setQrCode(response.data);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    chargeMutation.mutate({
      value: Math.round(parseFloat(amount) * 100).toString(),
      description: description || undefined,
    });
  };

  const handleClose = () => {
    setQrCode(null);
    setAmount('');
    setDescription('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Receber via Pix">
      {qrCode ? (
        <div className="text-center space-y-4">
          <img
            src={qrCode.qrCodeImage}
            alt="QR Code Pix"
            className="w-64 h-64 mx-auto rounded-lg"
          />
          <div className="p-4 bg-surface-800/50 rounded-lg">
            <p className="text-sm text-surface-400 mb-2">Código Pix Copia e Cola</p>
            <p className="font-mono text-xs text-white break-all">{qrCode.brCode}</p>
          </div>
          <Button onClick={handleClose} className="w-full">
            Fechar
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />

          <Input
            label="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex: Pagamento de serviço"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={chargeMutation.isPending}>
              {chargeMutation.isPending ? 'Gerando...' : 'Gerar QR Code'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// Statement Modal Component
function StatementModal({
  isOpen,
  onClose,
  accountId: _accountId,
  entries,
}: {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  entries: LedgerEntry[];
}) {
  const getEntryColor = (type: string) =>
    type === 'CREDIT' ? 'text-green-400' : type === 'DEBIT' ? 'text-red-400' : 'text-surface-400';

  const getEntryPrefix = (type: string) =>
    type === 'CREDIT' ? '+' : type === 'DEBIT' ? '-' : '';

  const getBadgeVariant = (type: string): 'success' | 'error' | 'warning' =>
    type === 'CREDIT' ? 'success' : type === 'DEBIT' ? 'error' : 'warning';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Extrato Completo" size="xl">
      <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
        {entries.length === 0 ? (
          <p className="text-center text-surface-400 py-8">Nenhuma movimentação encontrada</p>
        ) : (
          <>
            <p className="text-xs text-surface-500 mb-3">{entries.length} movimentações</p>
            {/* Mobile: Card layout */}
            <div className="md:hidden space-y-3">
              {entries.map((entry) => (
                <div key={entry.id} className="p-3 rounded-lg border border-surface-700 bg-surface-800/50 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant={getBadgeVariant(entry.type)}>
                        {entry.type === 'CREDIT' ? 'Crédito' : entry.type === 'DEBIT' ? 'Débito' : entry.type === 'BLOCK' ? 'Bloqueio' : entry.type === 'UNBLOCK' ? 'Desbloqueio' : entry.type}
                      </Badge>
                      <span className="text-xs text-surface-500 shrink-0">{formatDate(entry.createdAt)}</span>
                    </div>
                    <span className={cn('text-sm font-semibold shrink-0', getEntryColor(entry.type))}>
                      {getEntryPrefix(entry.type)}{formatCurrency(Number(entry.amount))}
                    </span>
                  </div>
                  <p className="text-sm text-surface-300 leading-snug break-words">{entry.description}</p>
                  <div className="flex items-center justify-between pt-1 border-t border-surface-700/50">
                    <span className="text-xs text-surface-500">Saldo após</span>
                    <span className="text-sm font-medium text-white">{formatCurrency(Number(entry.balanceAfter))}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-900 z-10">
                  <tr className="text-left text-surface-400">
                    <th className="pb-3 pr-3 whitespace-nowrap">Data/Hora</th>
                    <th className="pb-3 pr-3">Tipo</th>
                    <th className="pb-3 pr-3">Descrição</th>
                    <th className="pb-3 pr-3 text-right whitespace-nowrap">Saldo Antes</th>
                    <th className="pb-3 pl-3 text-right whitespace-nowrap">Valor</th>
                    <th className="pb-3 pl-3 text-right whitespace-nowrap">Saldo Após</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-t border-surface-700 hover:bg-surface-800/50">
                      <td className="py-3 pr-3 text-surface-300 whitespace-nowrap text-xs">{formatDate(entry.createdAt)}</td>
                      <td className="py-3 pr-3">
                        <Badge variant={getBadgeVariant(entry.type)}>
                          {entry.type === 'CREDIT' ? 'Crédito' : entry.type === 'DEBIT' ? 'Débito' : entry.type === 'BLOCK' ? 'Bloqueio' : entry.type === 'UNBLOCK' ? 'Desbloqueio' : entry.type}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3 text-white text-xs max-w-[300px]">
                        <p className="break-words">{entry.description}</p>
                        <p className="text-[10px] text-surface-600 mt-0.5 font-mono truncate">{entry.correlationId}</p>
                      </td>
                      <td className="py-3 pr-3 text-right text-surface-400 whitespace-nowrap">
                        {formatCurrency(Number(entry.balanceBefore))}
                      </td>
                      <td className={cn('py-3 pl-3 text-right font-semibold whitespace-nowrap', getEntryColor(entry.type))}>
                        {getEntryPrefix(entry.type)}{formatCurrency(Number(entry.amount))}
                      </td>
                      <td className="py-3 pl-3 text-right text-white font-medium whitespace-nowrap">
                        {formatCurrency(Number(entry.balanceAfter))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end pt-4 border-t border-surface-700 mt-4">
        <Button onClick={onClose} className="w-full sm:w-auto">Fechar</Button>
      </div>
    </Modal>
  );
}

export default VirtualAccounts;

// ============================================
// Custom Fee Card Component
// ============================================

function CustomFeeCard({ accountId }: { accountId: string }) {
  const [editing, setEditing] = useState(false);
  const [feeValue, setFeeValue] = useState('');
  const queryClient = useQueryClient();

  const { data: feeData, isLoading } = useQuery({
    queryKey: ['custom-fee', accountId],
    queryFn: () => api.get(`/virtual-accounts/${accountId}/custom-fee`).then(r => r.data),
    enabled: !!accountId,
  });

  const updateMutation = useMutation({
    mutationFn: (percentage: number | null) =>
      api.put(`/virtual-accounts/${accountId}/custom-fee`, { customCashInPercentage: percentage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fee', accountId] });
      setEditing(false);
    },
  });

  const handleSave = () => {
    const value = feeValue.trim();
    if (!value) {
      updateMutation.mutate(null); // Reset to default
    } else {
      const pct = parseFloat(value) / 100;
      if (isNaN(pct) || pct < 0.03 || pct > 0.50) {
        alert('Percentual deve estar entre 3% e 50%');
        return;
      }
      updateMutation.mutate(pct);
    }
  };

  const currentPct = feeData?.data?.effectivePercentage;
  const isCustom = feeData?.data?.isCustom;

  if (isLoading) return null;

  return (
    <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-surface-400">Taxa de Depósito</p>
          <p className={`text-lg font-bold ${isCustom ? 'text-amber-400' : 'text-surface-200'}`}>
            {currentPct ? `${(currentPct * 100).toFixed(1)}%` : '5.0%'}
            {isCustom && <span className="text-xs ml-2 text-amber-400/70">(personalizada)</span>}
            {!isCustom && <span className="text-xs ml-2 text-surface-500">(padrão)</span>}
          </p>
        </div>
        {!editing && (
          <button
            onClick={() => {
              setFeeValue(isCustom && currentPct ? (currentPct * 100).toFixed(1) : '');
              setEditing(true);
            }}
            className="px-3 py-1.5 text-xs rounded-lg bg-surface-700 text-surface-300 hover:text-white hover:bg-surface-600 transition-colors"
          >
            Alterar
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="number"
            step="0.1"
            min="3"
            max="50"
            placeholder="Ex: 8 (para 8%)"
            value={feeValue}
            onChange={e => setFeeValue(e.target.value)}
            className="flex-1 px-3 py-2 bg-surface-900 border border-surface-600 rounded-lg text-white text-sm focus:border-primary-500 focus:outline-none"
          />
          <span className="text-surface-400 text-sm">%</span>
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="px-3 py-2 rounded-lg bg-primary-500 text-white text-sm hover:bg-primary-600 disabled:opacity-50"
          >
            {updateMutation.isPending ? '...' : 'Salvar'}
          </button>
          <button
            onClick={() => { updateMutation.mutate(null); }}
            disabled={updateMutation.isPending}
            className="px-3 py-2 rounded-lg bg-surface-700 text-surface-300 text-sm hover:bg-surface-600"
            title="Voltar para padrão (5%)"
          >
            Padrão
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-2 py-2 text-surface-500 hover:text-surface-300"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
